import { Platform } from '@angular/cdk/platform';
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Tag, Track } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import { TracksTableDirective } from '@app/library/components/content/tracks-table/tracks-table.directive';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { BacsState } from '@app/library/states/bacs.state';
import { ToggleColumn } from '@app/library/states/columns.actions';
import { RemoveTagFromTrackRequest } from '@app/library/states/radio-tags.actions';
import { RadioTagsState } from '@app/library/states/radio-tags.state';
import {
  SelectTrack,
  UnselectAll,
  UnselectTrack,
} from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { delay, map, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'rk-tag-tracks-table',
  templateUrl: './tag-tracks-table.component.html',
  styleUrls: ['./tag-tracks-table.component.scss'],
})
export class TagTracksTableComponent
  extends TracksTableDirective
  implements AfterViewInit
{
  @Input()
  columns: ColumnShown;

  @Input()
  tag: Tag;

  @Input()
  searchField$: Observable<string>;

  @Select(SelectedTracksState.selectedTracks)
  selectedTracks$: Observable<number[]>;

  @Select(SelectedTracksState.numberTracksSelected)
  numberTracksSelected$: Observable<number>;

  constructor(
    protected readonly store: Store,
    protected readonly modalService: LibraryModalService,
    protected readonly actions$: Actions,
    protected readonly dialog: MatDialog,
    protected readonly hotkeys: HotkeysService,
    protected readonly cdr: ChangeDetectorRef,
    private readonly platform: Platform,
  ) {
    super(store, modalService, actions$, dialog, hotkeys, cdr);
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(SelectTrack),
          map(acc => acc.track.id),
        )
        .subscribe(idTrack => {
          this.lastSelectedTrackId = idTrack;
        }),
    );
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(UnselectTrack),
          map(acc => acc.track.id),
        )
        .subscribe(idTrack => {
          if (this.lastSelectedTrackId === idTrack) {
            this.lastSelectedTrackId = -1;
          }
        }),
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(UnselectAll)).subscribe(() => {
        this.lastSelectedTrackId = -1;
      }),
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(ToggleColumn), delay(100)).subscribe(() => {
        this.updateTagZoneSize();
      }),
    );
    this.selectedTracksObserver(this.numberTracksSelected$);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.subscription.add(
      this.resizeObservable.pipe(throttleTime(250)).subscribe(() => {
        if (this.store.selectSnapshot(SelectedTracksState.numberTracksSelected) > 0) {
          this.updateHeaderActions();
        }
      }),
    );
  }
  @Dispatch()
  deleteFromTag(track: Track) {
    return new RemoveTagFromTrackRequest(track);
  }

  archiveTrack(track: Track, shortcut = false) {
    const targetBac = this.store.selectSnapshot(BacsState.bacById)(track.idTrackbox);
    this.modalService.archiveTrack(track, targetBac, shortcut);
  }

  archiveMultiple(shortcut = false) {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (selectedTracks.length < 2) {
      const track = this.store.selectSnapshot(RadioTagsState.trackById)(
        selectedTracks[0],
      );
      this.archiveTrack(track, shortcut);
    } else {
      this.modalService.archiveMultipleTracks(
        this.store.selectSnapshot(SelectedTracksState.selectedTracks),
        null,
        shortcut,
      );
    }
  }

  getTableHeight(): number {
    // Size * line height + header height
    if (this.tracksObservable$.value.length) {
      return this.tracksObservable$.value.length * 50 + 43;
    }

    return 256;
  }

  detectScroll() {
    if (this.table && !this.platform.FIREFOX) {
      const totalTableHeight = this.getTableHeight() + 219;
      this.hasScroll = totalTableHeight > window.innerHeight;
    }
  }
}
